import { getDeployedEnvType, getNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import {
  getWebPromptDetailsInfo,
  phoenixAccountGetCustomerPhoneAndConsentDetails,
  webPromptOperationSignatures,
} from '@marriott/mi-store-utils';
import { ACCOUNT_AUTH_FLYOUT_MODAL_ID, ALL } from '../../../utils/constants';
import { ACCOUNT_CONTAINER_CENTRALIZED } from '@marriott/shared/mi-helper-utils';

export function isLongTextLocale(locale: string): boolean {
  const longTextLocales = ['de-DE'];
  return longTextLocales.includes(locale);
}

export const getIconClass = (iconClassTag: string): string => {
  const lastIndex = iconClassTag.lastIndexOf('/');
  if (lastIndex !== -1) {
    return iconClassTag.substring(lastIndex + 1);
  }
  return iconClassTag;
};

export function enableLoggedInPanel(obj?: Record<string, unknown>, currentUrl?: string): boolean {
  if (!obj || typeof obj !== 'object') return false;

  // Only return true if modalId matches AND smartHubEnabledPages exists and includes currentUrl
  const modalIdMatch = (obj as Record<string, unknown>)['modalId'] === ACCOUNT_AUTH_FLYOUT_MODAL_ID;
  const smartHubEnabledPages = (obj as Record<string, unknown>)['smartHubEnabledPages'] as string | undefined;

  if (modalIdMatch && smartHubEnabledPages && currentUrl) {
    const url = new URL(currentUrl, window.location.origin);
    const path = url.pathname + url.search;
    const enabled = smartHubEnabledPages
      .split(',')
      .map(s => s.trim())
      .some(enabledPath => enabledPath.toLowerCase() === ALL || path.includes(enabledPath));
    if (enabled) return true;
  }

  // Recursively check nested :items if present
  if (obj[':items']) {
    return Object.values(obj[':items'] as Record<string, unknown>).some(v =>
      enableLoggedInPanel(v as Record<string, unknown>, currentUrl)
    );
  }
  return Object.values(obj).some(value =>
    typeof value === 'object' && value !== null
      ? enableLoggedInPanel(value as Record<string, unknown>, currentUrl)
      : false
  );
}

export const hasCentralizedFlyoutContainer = (value: unknown): boolean => {
  return hasFlyoutContainerById(value, ACCOUNT_CONTAINER_CENTRALIZED);
};

export const hasFlyoutContainerById = (value: unknown, targetId: string): boolean => {
  if (!value || typeof value !== 'object') return false;

  const record = value as Record<string, unknown>;
  const containerId = record['id'];
  const containerModalId = record['modalId'];

  if (containerId === targetId || containerModalId === targetId) {
    return true;
  }

  return Object.values(record).some(child => hasFlyoutContainerById(child, targetId));
};

export const getCustomerPhoneAndConsentDetails = async (customerId?: string, locale?: string) => {
  if (!customerId) return null;
  const webPromptStoreData = await getWebPromptDetailsInfo({
    customerId,
    locale: locale || 'en-US',
    fetcher: async ({ customerId: uxlCustomerId, locale: uxlLocale }) => {
      const miApolloClient = getNextMiApolloClient(webPromptOperationSignatures, getDeployedEnvType());
      const { data } = await miApolloClient.query({
        query: phoenixAccountGetCustomerPhoneAndConsentDetails,
        variables: { customerId: uxlCustomerId },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
        context: {
          headers: {
            'accept-language': uxlLocale || 'en-US',
          },
        },
      });

      return data;
    },
  });
  return webPromptStoreData?.webPromptDetails;
};

export const findFlyoutEntry = (
  itemMap: Record<string, Record<string, unknown>> | undefined,
  targetId: string
): [string, Record<string, unknown>] | undefined => {
  if (!itemMap) return undefined;

  let recursiveMatch: [string, Record<string, unknown>] | undefined;

  for (const [key, item] of Object.entries(itemMap)) {
    if (!item || typeof item !== 'object') continue;

    const itemIdentifier = (item['id'] ?? item['modalId']) as string | undefined;
    if (itemIdentifier === targetId) {
      return [key, item];
    }

    if (!recursiveMatch && hasFlyoutContainerById(item, targetId)) {
      recursiveMatch = [key, item];
    }
  }

  return recursiveMatch;
};
